import React, { Suspense, lazy } from "react";
import { createBrowserRouter, Navigate, RouteObject } from "react-router-dom";
import PageLayout from "@/components/Layout/PageLayout";

// Lazy-loaded page components with named chunks
const Home = lazy(() => import(/* webpackChunkName: "home" */ "@/pages/Home/Home"));
const HomeEs = lazy(() => import(/* webpackChunkName: "home-es" */ "@/pages/Home/HomeEs"));
const About = lazy(() => import(/* webpackChunkName: "about" */ "@/pages/About/About"));
const AboutEs = lazy(() => import(/* webpackChunkName: "about-es" */ "@/pages/About/AboutEs"));
const Contact = lazy(() => import(/* webpackChunkName: "contact" */ "@/pages/Contact/Contact"));
const ContactEs = lazy(() => import(/* webpackChunkName: "contact-es" */ "@/pages/Contact/ContactEs"));
const NotFound = lazy(() => import(/* webpackChunkName: "not-found" */ "@/pages/NotFound/NotFound"));

// Hire Me (for recruiters)
const HireMe = lazy(() => import(/* webpackChunkName: "hire-me" */ "@/pages/HireMe/HireMe"));
const HireMeEs = lazy(() => import(/* webpackChunkName: "hire-me-es" */ "@/pages/HireMe/HireMeEs"));

// Business partner positions
const Cto = lazy(() => import(/* webpackChunkName: "cto" */ "@/pages/Cto/Cto"));
const CtoEs = lazy(() => import(/* webpackChunkName: "cto-es" */ "@/pages/Cto/CtoEs"));
const DigitalMarketingSpecialist = lazy(() => import(/* webpackChunkName: "dms" */ "@/pages/DigitalMarketingSpecialist/DigitalMarketingSpecialist"));
const DigitalMarketingSpecialistEs = lazy(() => import(/* webpackChunkName: "dms-es" */ "@/pages/DigitalMarketingSpecialist/DigitalMarketingSpecialistEs"));
const ForHire = lazy(() => import(/* webpackChunkName: "for-hire" */ "@/pages/ForHire/ForHire"));
const ForHireEs = lazy(() => import(/* webpackChunkName: "for-hire-es" */ "@/pages/ForHire/ForHireEs"));
const GrowthMarketingSpecialist = lazy(() => import(/* webpackChunkName: "growth-marketing-specialist" */ "@/pages/RolePages/GrowthMarketingSpecialist"));
const GrowthMarketingSpecialistEs = lazy(() => import(/* webpackChunkName: "growth-marketing-specialist-es" */ "@/pages/RolePages/GrowthMarketingSpecialistEs"));
const WebContentManager = lazy(() => import(/* webpackChunkName: "web-content-manager" */ "@/pages/RolePages/WebContentManager"));
const WebContentManagerEs = lazy(() => import(/* webpackChunkName: "web-content-manager-es" */ "@/pages/RolePages/WebContentManagerEs"));
const ContentManager = lazy(() => import(/* webpackChunkName: "content-manager" */ "@/pages/RolePages/ContentManager"));
const ContentManagerEs = lazy(() => import(/* webpackChunkName: "content-manager-es" */ "@/pages/RolePages/ContentManagerEs"));
const DigitalContentSpecialist = lazy(() => import(/* webpackChunkName: "digital-content-specialist" */ "@/pages/RolePages/DigitalContentSpecialist"));
const DigitalContentSpecialistEs = lazy(() => import(/* webpackChunkName: "digital-content-specialist-es" */ "@/pages/RolePages/DigitalContentSpecialistEs"));
const EcommerceContentSpecialist = lazy(() => import(/* webpackChunkName: "ecommerce-content-specialist" */ "@/pages/RolePages/EcommerceContentSpecialist"));
const EcommerceContentSpecialistEs = lazy(() => import(/* webpackChunkName: "ecommerce-content-specialist-es" */ "@/pages/RolePages/EcommerceContentSpecialistEs"));
const ContentMarketingManager = lazy(() => import(/* webpackChunkName: "content-marketing-manager" */ "@/pages/RolePages/ContentMarketingManager"));
const ContentMarketingManagerEs = lazy(() => import(/* webpackChunkName: "content-marketing-manager-es" */ "@/pages/RolePages/ContentMarketingManagerEs"));
const BusinessPartners = lazy(() => import(/* webpackChunkName: "business-partners" */ "@/pages/BusinessPartners/BusinessPartners"));
const BusinessPartnersEs = lazy(() => import(/* webpackChunkName: "business-partners-es" */ "@/pages/BusinessPartners/BusinessPartnersEs"));

// Free Google Check (flyer QR landing — door-to-door canvassing)
const GoogleCheck = lazy(() => import(/* webpackChunkName: "google-check" */ "@/pages/GoogleCheck/GoogleCheck"));
const GoogleCheckEs = lazy(() => import(/* webpackChunkName: "google-check-es" */ "@/pages/GoogleCheck/GoogleCheckEs"));

// Public agreement pages — the standard forms, published so a prospect can read the paper
// before booking a call. One component per language, slug supplied by the route.
const Agreements = lazy(() => import(/* webpackChunkName: "agreements" */ "@/pages/Agreements/Agreements"));
const AgreementsEs = lazy(() => import(/* webpackChunkName: "agreements-es" */ "@/pages/Agreements/AgreementsEs"));
const AgreementPage = lazy(() => import(/* webpackChunkName: "agreement" */ "@/pages/Agreements/AgreementPage"));
const AgreementPageEs = lazy(() => import(/* webpackChunkName: "agreement-es" */ "@/pages/Agreements/AgreementPageEs"));

// Legal Pages
const PrivacyPolicy = lazy(() => import(/* webpackChunkName: "privacy" */ "@/pages/PrivacyPolicy/PrivacyPolicy"));
const PrivacyPolicyEs = lazy(() => import(/* webpackChunkName: "privacy-es" */ "@/pages/PrivacyPolicy/PrivacyPolicyEs"));
const TermsOfService = lazy(() => import(/* webpackChunkName: "terms" */ "@/pages/TermsOfService/TermsOfService"));
const TermsOfServiceEs = lazy(() => import(/* webpackChunkName: "terms-es" */ "@/pages/TermsOfService/TermsOfServiceEs"));
const Accessibility = lazy(() => import(/* webpackChunkName: "accessibility" */ "@/pages/Accessibility/Accessibility"));
const AccessibilityEs = lazy(() => import(/* webpackChunkName: "accessibility-es" */ "@/pages/Accessibility/AccessibilityEs"));
const Reviews = lazy(() => import(/* webpackChunkName: "reviews" */ "@/pages/Reviews/Reviews"));
const ReviewsEs = lazy(() => import(/* webpackChunkName: "reviews-es" */ "@/pages/Reviews/ReviewsEs"));

// Services Hub
const Services = lazy(() => import(/* webpackChunkName: "services" */ "@/pages/Services/Services"));
const ServicesEs = lazy(() => import(/* webpackChunkName: "services-es" */ "@/pages/Services/ServicesEs"));

// Website Development
const WebsiteDevelopment = lazy(() => import(/* webpackChunkName: "svc-webdev" */ "@/pages/Services/WebsiteDevelopment/WebsiteDevelopment"));
const WebsiteDevelopmentEs = lazy(() => import(/* webpackChunkName: "svc-webdev-es" */ "@/pages/Services/WebsiteDevelopment/WebsiteDevelopmentEs"));

// Local SEO
const LocalSEO = lazy(() => import(/* webpackChunkName: "svc-local-seo" */ "@/pages/Services/LocalSEO/LocalSEO"));
const LocalSEOEs = lazy(() => import(/* webpackChunkName: "svc-local-seo-es" */ "@/pages/Services/LocalSEO/LocalSEOEs"));

// Technical SEO (still routable but unlinked from Services index)
const TechnicalSEO = lazy(() => import(/* webpackChunkName: "svc-tech-seo" */ "@/pages/Services/TechnicalSEO/TechnicalSEO"));
const TechnicalSEOEs = lazy(() => import(/* webpackChunkName: "svc-tech-seo-es" */ "@/pages/Services/TechnicalSEO/TechnicalSEOEs"));

// Google Business Profile
const GoogleBusinessProfile = lazy(() => import(/* webpackChunkName: "svc-gbp" */ "@/pages/Services/GoogleBusinessProfile/GoogleBusinessProfile"));
const GoogleBusinessProfileEs = lazy(() => import(/* webpackChunkName: "svc-gbp-es" */ "@/pages/Services/GoogleBusinessProfile/GoogleBusinessProfileEs"));

// Keyword Research
const KeywordResearch = lazy(() => import(/* webpackChunkName: "svc-keyword-research" */ "@/pages/Services/KeywordResearch/KeywordResearch"));
const KeywordResearchEs = lazy(() => import(/* webpackChunkName: "svc-keyword-research-es" */ "@/pages/Services/KeywordResearch/KeywordResearchEs"));

// Social Media Management
const SocialMediaManagement = lazy(() => import(/* webpackChunkName: "svc-social" */ "@/pages/Services/SocialMediaManagement/SocialMediaManagement"));
const SocialMediaManagementEs = lazy(() => import(/* webpackChunkName: "svc-social-es" */ "@/pages/Services/SocialMediaManagement/SocialMediaManagementEs"));

// Live Stream Production
const LiveStreamProduction = lazy(() => import(/* webpackChunkName: "svc-livestream" */ "@/pages/Services/LiveStreamProduction/LiveStreamProduction"));
const LiveStreamProductionEs = lazy(() => import(/* webpackChunkName: "svc-livestream-es" */ "@/pages/Services/LiveStreamProduction/LiveStreamProductionEs"));

// Chat Moderation — deliberately not in the footer or the services index; it is a
// shareable landing page reached from the live-stream-production page and search.
const ChatModeration = lazy(() => import(/* webpackChunkName: "svc-chatmod" */ "@/pages/Services/ChatModeration/ChatModeration"));
const ChatModerationEs = lazy(() => import(/* webpackChunkName: "svc-chatmod-es" */ "@/pages/Services/ChatModeration/ChatModerationEs"));
const RemoteStreamProduction = lazy(() => import(/* webpackChunkName: "svc-remoteprod" */ "@/pages/Services/RemoteStreamProduction/RemoteStreamProduction"));
const RemoteStreamProductionEs = lazy(() => import(/* webpackChunkName: "svc-remoteprod-es" */ "@/pages/Services/RemoteStreamProduction/RemoteStreamProductionEs"));

// Growth Marketing
const GrowthMarketing = lazy(() => import(/* webpackChunkName: "svc-growth" */ "@/pages/Services/GrowthMarketing/GrowthMarketing"));
const GrowthMarketingEs = lazy(() => import(/* webpackChunkName: "svc-growth-es" */ "@/pages/Services/GrowthMarketing/GrowthMarketingEs"));

// Digital Marketing
const DigitalMarketing = lazy(() => import(/* webpackChunkName: "svc-digital" */ "@/pages/Services/DigitalMarketing/DigitalMarketing"));
const DigitalMarketingEs = lazy(() => import(/* webpackChunkName: "svc-digital-es" */ "@/pages/Services/DigitalMarketing/DigitalMarketingEs"));

// Photography
const Photography = lazy(() => import(/* webpackChunkName: "svc-photography" */ "@/pages/Services/Photography/Photography"));
const PhotographyEs = lazy(() => import(/* webpackChunkName: "svc-photography-es" */ "@/pages/Services/Photography/PhotographyEs"));
const FoodPhotography = lazy(() => import(/* webpackChunkName: "svc-photography-food" */ "@/pages/Services/Photography/FoodPhotography"));
const FoodPhotographyEs = lazy(() => import(/* webpackChunkName: "svc-photography-food-es" */ "@/pages/Services/Photography/FoodPhotographyEs"));
const ModelPhotography = lazy(() => import(/* webpackChunkName: "svc-photography-model" */ "@/pages/Services/Photography/ModelPhotography"));
const ModelPhotographyEs = lazy(() => import(/* webpackChunkName: "svc-photography-model-es" */ "@/pages/Services/Photography/ModelPhotographyEs"));

// Industry-specific service pillars
const LawFirmWebsites = lazy(() => import(/* webpackChunkName: "svc-law-firm-websites" */ "@/pages/Services/LawFirmWebsites/LawFirmWebsites"));
const LawFirmWebsitesEs = lazy(() => import(/* webpackChunkName: "svc-law-firm-websites-es" */ "@/pages/Services/LawFirmWebsites/LawFirmWebsitesEs"));
const EcommerceWebsites = lazy(() => import(/* webpackChunkName: "svc-ecommerce-websites" */ "@/pages/Services/EcommerceWebsites/EcommerceWebsites"));
const EcommerceWebsitesEs = lazy(() => import(/* webpackChunkName: "svc-ecommerce-websites-es" */ "@/pages/Services/EcommerceWebsites/EcommerceWebsitesEs"));
const ShopifyWebsiteDevelopment = lazy(() => import(/* webpackChunkName: "svc-shopify-website-development" */ "@/pages/Services/ShopifyWebsiteDevelopment/ShopifyWebsiteDevelopment"));
const ShopifyWebsiteDevelopmentEs = lazy(() => import(/* webpackChunkName: "svc-shopify-website-development-es" */ "@/pages/Services/ShopifyWebsiteDevelopment/ShopifyWebsiteDevelopmentEs"));

// Mobile App Development
const MobileAppDevelopment = lazy(() => import(/* webpackChunkName: "svc-mobile-apps" */ "@/pages/Services/MobileAppDevelopment/MobileAppDevelopment"));
const MobileAppDevelopmentEs = lazy(() => import(/* webpackChunkName: "svc-mobile-apps-es" */ "@/pages/Services/MobileAppDevelopment/MobileAppDevelopmentEs"));

// Brand Development (influencers & creators)
const BrandDevelopment = lazy(() => import(/* webpackChunkName: "svc-brand-dev" */ "@/pages/Services/BrandDevelopment/BrandDevelopment"));
const BrandDevelopmentEs = lazy(() => import(/* webpackChunkName: "svc-brand-dev-es" */ "@/pages/Services/BrandDevelopment/BrandDevelopmentEs"));

// AWS Cloud Solutions
const AwsCloudSolutions = lazy(() => import(/* webpackChunkName: "svc-aws-cloud" */ "@/pages/Services/AwsCloudSolutions/AwsCloudSolutions"));
const AwsCloudSolutionsEs = lazy(() => import(/* webpackChunkName: "svc-aws-cloud-es" */ "@/pages/Services/AwsCloudSolutions/AwsCloudSolutionsEs"));

// Products (Baccarat Smart + in-house software showcase)
const Products = lazy(() => import(/* webpackChunkName: "products" */ "@/pages/Products/Products"));
const ProductsEs = lazy(() => import(/* webpackChunkName: "products-es" */ "@/pages/Products/ProductsEs"));

// Industry hubs (linked from the homepage IndustriesStrip; prerendered in routes-meta)
const Industries = lazy(() => import(/* webpackChunkName: "industries" */ "@/pages/Industries/Industries"));
const IndustriesEs = lazy(() => import(/* webpackChunkName: "industries-es" */ "@/pages/Industries/IndustriesEs"));
const Restaurants = lazy(() => import(/* webpackChunkName: "ind-restaurants" */ "@/pages/Industries/Restaurants"));
const RestaurantsEs = lazy(() => import(/* webpackChunkName: "ind-restaurants-es" */ "@/pages/Industries/RestaurantsEs"));
const Attorneys = lazy(() => import(/* webpackChunkName: "ind-attorneys" */ "@/pages/Industries/Attorneys"));
const AttorneysEs = lazy(() => import(/* webpackChunkName: "ind-attorneys-es" */ "@/pages/Industries/AttorneysEs"));
const IndustryEcommerce = lazy(() => import(/* webpackChunkName: "ind-ecommerce" */ "@/pages/Industries/Ecommerce"));
const IndustryEcommerceEs = lazy(() => import(/* webpackChunkName: "ind-ecommerce-es" */ "@/pages/Industries/EcommerceEs"));
const FashionBrands = lazy(() => import(/* webpackChunkName: "ind-fashion-brands" */ "@/pages/Industries/FashionBrands"));
const FashionBrandsEs = lazy(() => import(/* webpackChunkName: "ind-fashion-brands-es" */ "@/pages/Industries/FashionBrandsEs"));
const Salons = lazy(() => import(/* webpackChunkName: "ind-salons" */ "@/pages/Industries/Salons"));
const SalonsEs = lazy(() => import(/* webpackChunkName: "ind-salons-es" */ "@/pages/Industries/SalonsEs"));
const ChildCare = lazy(() => import(/* webpackChunkName: "ind-child-care" */ "@/pages/Industries/ChildCare"));
const ChildCareEs = lazy(() => import(/* webpackChunkName: "ind-child-care-es" */ "@/pages/Industries/ChildCareEs"));

// Portfolio (production work showcase — websites are the foundational service)
const Portfolio = lazy(() => import(/* webpackChunkName: "portfolio" */ "@/pages/Portfolio/Portfolio"));
const PortfolioEs = lazy(() => import(/* webpackChunkName: "portfolio-es" */ "@/pages/Portfolio/PortfolioEs"));

// Free website audit (PSI-powered lead capture — scores emailed by Lambda)
const WebsiteAudit = lazy(() => import(/* webpackChunkName: "website-audit" */ "@/pages/WebsiteAudit/WebsiteAudit"));
const WebsiteAuditEs = lazy(() => import(/* webpackChunkName: "website-audit-es" */ "@/pages/WebsiteAudit/WebsiteAuditEs"));

// Set up a 15-min call (qualifying form before scheduling)
const Book = lazy(() => import(/* webpackChunkName: "book" */ "@/pages/Book/Book"));
const BookEs = lazy(() => import(/* webpackChunkName: "book-es" */ "@/pages/Book/BookEs"));
const BookThankYou = lazy(() => import(/* webpackChunkName: "book-thank-you" */ "@/pages/Book/BookThankYou"));
const BookThankYouEs = lazy(() => import(/* webpackChunkName: "book-thank-you-es" */ "@/pages/Book/BookThankYouEs"));

// Links hub (in-house Linktree). QR target for canvassing + IG bio.
const Links = lazy(() => import(/* webpackChunkName: "links" */ "@/pages/Links/Links"));
const LinksEs = lazy(() => import(/* webpackChunkName: "links-es" */ "@/pages/Links/LinksEs"));

// Live (Kick streaming)
const Live = lazy(() => import(/* webpackChunkName: "live" */ "@/pages/Live/Live"));
const LiveEs = lazy(() => import(/* webpackChunkName: "live-es" */ "@/pages/Live/LiveEs"));

// Site directory (visitor-facing page index — NOT sitemap.xml)
const Directory = lazy(() => import(/* webpackChunkName: "directory" */ "@/pages/Directory/Directory"));
const DirectoryEs = lazy(() => import(/* webpackChunkName: "directory-es" */ "@/pages/Directory/DirectoryEs"));

// Sample monthly report — what a retainer client receives every month
const SampleReport = lazy(() => import(/* webpackChunkName: "sample-report" */ "@/pages/SampleReport/SampleReport"));
const SampleReportEs = lazy(() => import(/* webpackChunkName: "sample-report-es" */ "@/pages/SampleReport/SampleReportEs"));

// Blog — index plus wave-1 posts. Each post is its own chunk so the index stays light.
const Blog = lazy(() => import(/* webpackChunkName: "blog" */ "@/pages/Blog/Blog"));
const BlogEs = lazy(() => import(/* webpackChunkName: "blog-es" */ "@/pages/Blog/BlogEs"));
const CoordinatorVsRetainer = lazy(() => import(/* webpackChunkName: "blog-coordinator-vs-retainer" */ "@/pages/Blog/CoordinatorVsRetainer/CoordinatorVsRetainer"));
const CoordinatorVsRetainerEs = lazy(() => import(/* webpackChunkName: "blog-coordinator-vs-retainer-es" */ "@/pages/Blog/CoordinatorVsRetainer/CoordinatorVsRetainerEs"));
const LocalSeoCost = lazy(() => import(/* webpackChunkName: "blog-local-seo-cost" */ "@/pages/Blog/LocalSeoCost/LocalSeoCost"));
const LocalSeoCostEs = lazy(() => import(/* webpackChunkName: "blog-local-seo-cost-es" */ "@/pages/Blog/LocalSeoCost/LocalSeoCostEs"));
const WebsiteAuditAnatomy = lazy(() => import(/* webpackChunkName: "blog-website-audit-anatomy" */ "@/pages/Blog/WebsiteAuditAnatomy/WebsiteAuditAnatomy"));
const WebsiteAuditAnatomyEs = lazy(() => import(/* webpackChunkName: "blog-website-audit-anatomy-es" */ "@/pages/Blog/WebsiteAuditAnatomy/WebsiteAuditAnatomyEs"));
const TrafficNoLeads = lazy(() => import(/* webpackChunkName: "blog-traffic-no-leads" */ "@/pages/Blog/TrafficNoLeads/TrafficNoLeads"));
const TrafficNoLeadsEs = lazy(() => import(/* webpackChunkName: "blog-traffic-no-leads-es" */ "@/pages/Blog/TrafficNoLeads/TrafficNoLeadsEs"));


// Loading component. `data-route-loading` lets the prerendered static-hero
// splash (scripts/optimize-html-head.mjs) stay painted until the REAL page —
// not this Suspense spinner — commits into #root. See webpack-performance.md.
const PageLoader: React.FC = () => (
  <div
    data-route-loading="true"
    className="flex items-center justify-center min-h-screen"
  >
    <div className="animate-spin rounded-full h-12 w-12 border-4 border-metro-orange-500 border-t-transparent" />
  </div>
);

// Wrap components with Suspense
const withSuspense = (Component: React.LazyExoticComponent<React.FC>) => (
  <Suspense fallback={<PageLoader />}>
    <Component />
  </Suspense>
);

// Same, for the agreement pages: one component renders any agreement, and the slug comes
// from the route rather than a path param so `webpack/config/routes-meta.js` can carry real
// per-path SEO metadata and the prerender pass can emit static HTML for each one.
const withSuspenseSlug = (
  Component: React.LazyExoticComponent<React.FC<{ slug: string }>>,
  slug: string
) => (
  <Suspense fallback={<PageLoader />}>
    <Component slug={slug} />
  </Suspense>
);

// Route definitions
const routes: RouteObject[] = [
  // Chrome-free routes (no Header/Footer) — Linktree-style immersive pages.
  // Must come BEFORE the PageLayout branch so React Router matches them first.
  { path: "/links", element: withSuspense(Links) },
  { path: "/es/enlaces", element: withSuspense(LinksEs) },

  {
    element: <PageLayout />,
    children: [
      // English routes
      { path: "/", element: withSuspense(Home) },
      { path: "/about", element: withSuspense(About) },
      { path: "/contact", element: withSuspense(Contact) },
      { path: "/hire-me", element: withSuspense(HireMe) },
      { path: "/google-check", element: withSuspense(GoogleCheck) },
      { path: "/for-hire/chief-technology-officer", element: withSuspense(Cto) },
      { path: "/for-hire/cto", element: <Navigate to="/for-hire/chief-technology-officer" replace /> },
      {
        path: "/for-hire/digital-marketing-specialist",
        element: withSuspense(DigitalMarketingSpecialist),
      },
      { path: "/for-hire", element: withSuspense(ForHire) },
      {
        path: "/for-hire/growth-marketing-specialist",
        element: withSuspense(GrowthMarketingSpecialist),
      },
      // Legacy root-level role URLs → /for-hire/* (moved 2026-07-30)
      { path: "/digital-marketing-specialist", element: <Navigate to="/for-hire/digital-marketing-specialist" replace /> },
      { path: "/cto", element: <Navigate to="/for-hire/chief-technology-officer" replace /> },
      { path: "/web-content-manager", element: <Navigate to="/for-hire/web-content-manager" replace /> },
      { path: "/content-manager", element: <Navigate to="/for-hire/content-manager" replace /> },
      { path: "/digital-content-specialist", element: <Navigate to="/for-hire/digital-content-specialist" replace /> },
      { path: "/ecommerce-content-specialist", element: <Navigate to="/for-hire/ecommerce-content-specialist" replace /> },
      { path: "/content-marketing-manager", element: <Navigate to="/for-hire/content-marketing-manager" replace /> },
      {
        path: "/for-hire/web-content-manager",
        element: withSuspense(WebContentManager),
      },
      {
        path: "/for-hire/content-manager",
        element: withSuspense(ContentManager),
      },
      {
        path: "/for-hire/digital-content-specialist",
        element: withSuspense(DigitalContentSpecialist),
      },
      {
        path: "/for-hire/ecommerce-content-specialist",
        element: withSuspense(EcommerceContentSpecialist),
      },
      {
        path: "/for-hire/content-marketing-manager",
        element: withSuspense(ContentMarketingManager),
      },
      { path: "/business-partners", element: withSuspense(BusinessPartners) },

      // Legal Pages (English)
      { path: "/privacy-policy", element: withSuspense(PrivacyPolicy) },
      { path: "/terms-of-service", element: withSuspense(TermsOfService) },
      { path: "/accessibility", element: withSuspense(Accessibility) },

      // Google reviews — proof plus the review ask (English)
      { path: "/reviews", element: withSuspense(Reviews) },

      // Public agreements (English)
      { path: "/agreements", element: withSuspense(Agreements) },
      {
        path: "/agreements/website-plan",
        element: withSuspenseSlug(AgreementPage, "website-plan"),
      },
      {
        path: "/agreements/online-store-plan",
        element: withSuspenseSlug(AgreementPage, "online-store-plan"),
      },
      {
        path: "/agreements/google-maps-optimization",
        element: withSuspenseSlug(AgreementPage, "google-maps-optimization"),
      },
      {
        path: "/agreements/social-media-management",
        element: withSuspenseSlug(AgreementPage, "social-media-management"),
      },
      {
        path: "/agreements/local-seo-pages",
        element: withSuspenseSlug(AgreementPage, "local-seo-pages"),
      },
      {
        path: "/agreements/marketing-specialist-retainer",
        element: withSuspenseSlug(AgreementPage, "marketing-specialist-retainer"),
      },
      {
        path: "/agreements/fixed-scope-project",
        element: withSuspenseSlug(AgreementPage, "fixed-scope-project"),
      },

      // Live (Kick streaming)
      { path: "/live", element: withSuspense(Live) },
      { path: "/episodes", element: <Navigate to="/live" replace /> },

      // English Service Routes
      { path: "/services", element: withSuspense(Services) },
      {
        path: "/services/website-development",
        element: withSuspense(WebsiteDevelopment),
      },
      { path: "/services/local-seo", element: withSuspense(LocalSEO) },
      { path: "/services/technical-seo", element: withSuspense(TechnicalSEO) },
      {
        path: "/services/google-business-profile",
        element: withSuspense(GoogleBusinessProfile),
      },
      {
        path: "/services/keyword-research",
        element: withSuspense(KeywordResearch),
      },
      {
        path: "/services/social-media-management",
        element: withSuspense(SocialMediaManagement),
      },
      {
        path: "/services/live-stream-production",
        element: withSuspense(LiveStreamProduction),
      },
      {
        path: "/services/chat-moderation",
        element: withSuspense(ChatModeration),
      },
      {
        path: "/services/remote-stream-production",
        element: withSuspense(RemoteStreamProduction),
      },
      {
        path: "/services/growth-marketing",
        element: withSuspense(GrowthMarketing),
      },
      {
        path: "/services/digital-marketing",
        element: withSuspense(DigitalMarketing),
      },
      {
        path: "/services/photography",
        element: withSuspense(Photography),
      },
      {
        path: "/services/photography/food",
        element: withSuspense(FoodPhotography),
      },
      {
        path: "/services/photography/model",
        element: withSuspense(ModelPhotography),
      },

      // Industry-specific service pillars (SEO-targeted)
      {
        path: "/services/law-firm-websites",
        element: withSuspense(LawFirmWebsites),
      },
      // /services/law-firm-seo merged into law-firm-websites (combined pillar)
      {
        path: "/services/law-firm-seo",
        element: <Navigate to="/services/law-firm-websites" replace />,
      },
      {
        path: "/services/ecommerce-websites",
        element: withSuspense(EcommerceWebsites),
      },
      {
        path: "/services/shopify-website-development",
        element: withSuspense(ShopifyWebsiteDevelopment),
      },
      {
        path: "/services/mobile-app-development",
        element: withSuspense(MobileAppDevelopment),
      },
      {
        path: "/services/brand-development",
        element: withSuspense(BrandDevelopment),
      },
      {
        path: "/services/aws-cloud-solutions",
        element: withSuspense(AwsCloudSolutions),
      },

      // Products (in-house software showcase)
      { path: "/products", element: withSuspense(Products) },

      // Industry hubs (English)
      { path: "/industries", element: withSuspense(Industries) },
      { path: "/industries/restaurants", element: withSuspense(Restaurants) },
      { path: "/industries/attorneys", element: withSuspense(Attorneys) },
      { path: "/industries/ecommerce", element: withSuspense(IndustryEcommerce) },
      { path: "/industries/fashion-brands", element: withSuspense(FashionBrands) },
      { path: "/industries/salons", element: withSuspense(Salons) },
      { path: "/industries/child-care", element: withSuspense(ChildCare) },

      // Portfolio (production work showcase)
      { path: "/portfolio", element: withSuspense(Portfolio) },

      // Free website audit (PSI-powered lead capture)
      { path: "/website-audit", element: withSuspense(WebsiteAudit) },

      // Set up a 15-min call (canvassing-friendly short URL)
      { path: "/book", element: withSuspense(Book) },
      { path: "/book/thank-you", element: withSuspense(BookThankYou) },

      // Site directory (visitor-facing page index — NOT sitemap.xml)
      { path: "/directory", element: withSuspense(Directory) },

      // Sample monthly report
      { path: "/sample-report", element: withSuspense(SampleReport) },

      // Blog (English)
      { path: "/blog", element: withSuspense(Blog) },
      {
        path: "/blog/marketing-coordinator-vs-retainer",
        element: withSuspense(CoordinatorVsRetainer),
      },
      {
        path: "/blog/local-seo-cost-dc-area",
        element: withSuspense(LocalSeoCost),
      },
      {
        path: "/blog/anatomy-of-a-website-audit",
        element: withSuspense(WebsiteAuditAnatomy),
      },
      {
        path: "/blog/website-traffic-no-leads",
        element: withSuspense(TrafficNoLeads),
      },

      // Spanish Live
      { path: "/es/en-vivo", element: withSuspense(LiveEs) },
      { path: "/es/episodios", element: <Navigate to="/es/en-vivo" replace /> },

      // Spanish routes (reactivated 2026-05-13 — Spanish is a strategic differentiator,
      // not a soft-archive item; restored after content audit + CTA repoint to /es/contacto form).
      { path: "/es", element: withSuspense(HomeEs) },
      { path: "/es/sobre-nosotros", element: withSuspense(AboutEs) },
      { path: "/es/contacto", element: withSuspense(ContactEs) },
      { path: "/es/para-contratar/director-de-tecnologia", element: withSuspense(CtoEs) },
      { path: "/es/para-contratar/cto", element: <Navigate to="/es/para-contratar/director-de-tecnologia" replace /> },
      {
        path: "/es/para-contratar/especialista-en-marketing-digital",
        element: withSuspense(DigitalMarketingSpecialistEs),
      },
      { path: "/es/para-contratar", element: withSuspense(ForHireEs) },
      {
        path: "/es/para-contratar/especialista-en-marketing-de-crecimiento",
        element: withSuspense(GrowthMarketingSpecialistEs),
      },
      // Legacy Spanish role URLs → /es/para-contratar/* (moved 2026-07-30)
      { path: "/es/especialista-en-marketing-digital", element: <Navigate to="/es/para-contratar/especialista-en-marketing-digital" replace /> },
      { path: "/es/cto", element: <Navigate to="/es/para-contratar/director-de-tecnologia" replace /> },
      { path: "/es/gestor-de-contenido-web", element: <Navigate to="/es/para-contratar/gestor-de-contenido-web" replace /> },
      { path: "/es/gestor-de-contenidos", element: <Navigate to="/es/para-contratar/gestor-de-contenidos" replace /> },
      { path: "/es/especialista-en-contenido-digital", element: <Navigate to="/es/para-contratar/especialista-en-contenido-digital" replace /> },
      { path: "/es/especialista-en-contenido-ecommerce", element: <Navigate to="/es/para-contratar/especialista-en-contenido-ecommerce" replace /> },
      { path: "/es/gerente-de-marketing-de-contenidos", element: <Navigate to="/es/para-contratar/gerente-de-marketing-de-contenidos" replace /> },
      {
        path: "/es/para-contratar/gestor-de-contenido-web",
        element: withSuspense(WebContentManagerEs),
      },
      {
        path: "/es/para-contratar/gestor-de-contenidos",
        element: withSuspense(ContentManagerEs),
      },
      {
        path: "/es/para-contratar/especialista-en-contenido-digital",
        element: withSuspense(DigitalContentSpecialistEs),
      },
      {
        path: "/es/para-contratar/especialista-en-contenido-ecommerce",
        element: withSuspense(EcommerceContentSpecialistEs),
      },
      {
        path: "/es/para-contratar/gerente-de-marketing-de-contenidos",
        element: withSuspense(ContentMarketingManagerEs),
      },
      {
        path: "/es/socios-de-negocio",
        element: withSuspense(BusinessPartnersEs),
      },

      // Legal Pages (Spanish)
      {
        path: "/es/politica-de-privacidad",
        element: withSuspense(PrivacyPolicyEs),
      },
      {
        path: "/es/terminos-de-servicio",
        element: withSuspense(TermsOfServiceEs),
      },
      { path: "/es/accesibilidad", element: withSuspense(AccessibilityEs) },

      // Google reviews — proof plus the review ask (Spanish)
      { path: "/es/resenas", element: withSuspense(ReviewsEs) },

      // Public agreements (Spanish) — localized slugs; the terms themselves are executed
      // in English, which each page states plainly.
      { path: "/es/acuerdos", element: withSuspense(AgreementsEs) },
      {
        path: "/es/acuerdos/plan-de-sitio-web",
        element: withSuspenseSlug(AgreementPageEs, "website-plan"),
      },
      {
        path: "/es/acuerdos/plan-de-tienda-en-linea",
        element: withSuspenseSlug(AgreementPageEs, "online-store-plan"),
      },
      {
        path: "/es/acuerdos/optimizacion-google-maps",
        element: withSuspenseSlug(AgreementPageEs, "google-maps-optimization"),
      },
      {
        path: "/es/acuerdos/gestion-de-redes-sociales",
        element: withSuspenseSlug(AgreementPageEs, "social-media-management"),
      },
      {
        path: "/es/acuerdos/paginas-de-seo-local",
        element: withSuspenseSlug(AgreementPageEs, "local-seo-pages"),
      },
      {
        path: "/es/acuerdos/contrato-de-especialista-en-marketing",
        element: withSuspenseSlug(AgreementPageEs, "marketing-specialist-retainer"),
      },
      {
        path: "/es/acuerdos/proyecto-de-alcance-fijo",
        element: withSuspenseSlug(AgreementPageEs, "fixed-scope-project"),
      },

      // Spanish Service Routes (reactivated 2026-05-13)
      { path: "/es/servicios", element: withSuspense(ServicesEs) },
      {
        path: "/es/servicios/desarrollo-web",
        element: withSuspense(WebsiteDevelopmentEs),
      },
      {
        path: "/es/servicios/seo-local",
        element: withSuspense(LocalSEOEs),
      },
      {
        path: "/es/servicios/seo-tecnico",
        element: withSuspense(TechnicalSEOEs),
      },
      {
        path: "/es/servicios/google-business-profile",
        element: withSuspense(GoogleBusinessProfileEs),
      },
      {
        path: "/es/servicios/investigacion-de-palabras-clave",
        element: withSuspense(KeywordResearchEs),
      },
      {
        path: "/es/servicios/gestion-redes-sociales",
        element: withSuspense(SocialMediaManagementEs),
      },
      {
        path: "/es/servicios/transmisiones-en-vivo",
        element: withSuspense(LiveStreamProductionEs),
      },
      {
        path: "/es/servicios/moderacion-de-chat",
        element: withSuspense(ChatModerationEs),
      },
      {
        path: "/es/servicios/produccion-remota",
        element: withSuspense(RemoteStreamProductionEs),
      },
      {
        path: "/es/servicios/marketing-de-crecimiento",
        element: withSuspense(GrowthMarketingEs),
      },
      {
        path: "/es/servicios/marketing-digital",
        element: withSuspense(DigitalMarketingEs),
      },
      {
        path: "/es/servicios/fotografia",
        element: withSuspense(PhotographyEs),
      },
      {
        path: "/es/servicios/fotografia/comida",
        element: withSuspense(FoodPhotographyEs),
      },
      {
        path: "/es/servicios/fotografia/modelo",
        element: withSuspense(ModelPhotographyEs),
      },
      {
        path: "/es/servicios/sitios-web-bufetes",
        element: withSuspense(LawFirmWebsitesEs),
      },
      {
        path: "/es/servicios/sitios-web-ecommerce",
        element: withSuspense(EcommerceWebsitesEs),
      },
      {
        path: "/es/servicios/desarrollo-web-shopify",
        element: withSuspense(ShopifyWebsiteDevelopmentEs),
      },
      {
        path: "/es/servicios/desarrollo-de-apps-moviles",
        element: withSuspense(MobileAppDevelopmentEs),
      },
      {
        path: "/es/servicios/desarrollo-de-marca",
        element: withSuspense(BrandDevelopmentEs),
      },
      {
        path: "/es/servicios/soluciones-aws",
        element: withSuspense(AwsCloudSolutionsEs),
      },

      // Spanish Products
      { path: "/es/productos", element: withSuspense(ProductsEs) },

      // Industry hubs (Spanish)
      { path: "/es/industrias", element: withSuspense(IndustriesEs) },
      { path: "/es/industrias/restaurantes", element: withSuspense(RestaurantsEs) },
      { path: "/es/industrias/abogados", element: withSuspense(AttorneysEs) },
      { path: "/es/industrias/ecommerce", element: withSuspense(IndustryEcommerceEs) },
      { path: "/es/industrias/marcas-de-moda", element: withSuspense(FashionBrandsEs) },
      { path: "/es/industrias/salones", element: withSuspense(SalonsEs) },
      { path: "/es/industrias/cuidado-infantil", element: withSuspense(ChildCareEs) },

      // Spanish Portfolio
      { path: "/es/portafolio", element: withSuspense(PortfolioEs) },

      // Spanish Website Audit
      { path: "/es/auditoria-web", element: withSuspense(WebsiteAuditEs) },

      // Spanish Hire Me (recruiter / freelance landing)
      { path: "/es/contratame", element: withSuspense(HireMeEs) },
      { path: "/es/revision-google", element: withSuspense(GoogleCheckEs) },

      // Spanish Book funnel
      { path: "/es/agendar", element: withSuspense(BookEs) },
      { path: "/es/agendar/gracias", element: withSuspense(BookThankYouEs) },

      // Spanish site directory
      { path: "/es/directorio", element: withSuspense(DirectoryEs) },

      // Spanish sample monthly report
      { path: "/es/reporte-de-muestra", element: withSuspense(SampleReportEs) },

      // Blog (Spanish)
      { path: "/es/blog", element: withSuspense(BlogEs) },
      {
        path: "/es/blog/coordinador-de-marketing-o-iguala",
        element: withSuspense(CoordinatorVsRetainerEs),
      },
      {
        path: "/es/blog/costo-seo-local-dc",
        element: withSuspense(LocalSeoCostEs),
      },
      {
        path: "/es/blog/anatomia-de-una-auditoria-web",
        element: withSuspense(WebsiteAuditAnatomyEs),
      },
      {
        path: "/es/blog/trafico-sin-clientes",
        element: withSuspense(TrafficNoLeadsEs),
      },

      // 404
      { path: "*", element: withSuspense(NotFound) },
    ],
  },
];

export const router = createBrowserRouter(routes);

// Export route paths for type safety
export const routePaths = {
  // English
  home: "/",
  about: "/about",
  contact: "/contact",
  hireMe: "/hire-me",
  forHire: "/for-hire",
  growthMarketingSpecialist: "/for-hire/growth-marketing-specialist",
  googleCheck: "/google-check",
  cto: "/for-hire/chief-technology-officer",
  digitalMarketingSpecialist: "/for-hire/digital-marketing-specialist",
  webContentManager: "/for-hire/web-content-manager",
  contentManager: "/for-hire/content-manager",
  digitalContentSpecialist: "/for-hire/digital-content-specialist",
  ecommerceContentSpecialist: "/for-hire/ecommerce-content-specialist",
  contentMarketingManager: "/for-hire/content-marketing-manager",
  businessPartners: "/business-partners",
  privacyPolicy: "/privacy-policy",
  termsOfService: "/terms-of-service",
  accessibility: "/accessibility",
  reviews: "/reviews",
  agreements: "/agreements",
  agreementWebsitePlan: "/agreements/website-plan",
  agreementOnlineStorePlan: "/agreements/online-store-plan",
  agreementGoogleMaps: "/agreements/google-maps-optimization",
  agreementSocialMedia: "/agreements/social-media-management",
  agreementLocalSeoPages: "/agreements/local-seo-pages",
  agreementSpecialistRetainer: "/agreements/marketing-specialist-retainer",
  agreementFixedScopeProject: "/agreements/fixed-scope-project",
  services: "/services",
  websiteDevelopment: "/services/website-development",
  localSeo: "/services/local-seo",
  technicalSeo: "/services/technical-seo",
  googleBusinessProfile: "/services/google-business-profile",
  keywordResearch: "/services/keyword-research",
  socialMediaManagement: "/services/social-media-management",
  liveStreamProduction: "/services/live-stream-production",
  chatModeration: "/services/chat-moderation",
  remoteStreamProduction: "/services/remote-stream-production",
  growthMarketing: "/services/growth-marketing",
  digitalMarketing: "/services/digital-marketing",
  photography: "/services/photography",
  foodPhotography: "/services/photography/food",
  modelPhotography: "/services/photography/model",
  lawFirmWebsites: "/services/law-firm-websites",
  ecommerceWebsites: "/services/ecommerce-websites",
  shopifyWebsiteDevelopment: "/services/shopify-website-development",
  mobileAppDevelopment: "/services/mobile-app-development",
  brandDevelopment: "/services/brand-development",
  awsCloudSolutions: "/services/aws-cloud-solutions",
  products: "/products",
  industries: "/industries",
  industriesRestaurants: "/industries/restaurants",
  industriesAttorneys: "/industries/attorneys",
  industriesEcommerce: "/industries/ecommerce",
  industriesFashionBrands: "/industries/fashion-brands",
  industriesSalons: "/industries/salons",
  industriesChildCare: "/industries/child-care",
  portfolio: "/portfolio",
  websiteAudit: "/website-audit",
  book: "/book",
  bookThankYou: "/book/thank-you",
  live: "/live",
  links: "/links",
  directory: "/directory",
  sampleReport: "/sample-report",
  blog: "/blog",
  blogCoordinatorVsRetainer: "/blog/marketing-coordinator-vs-retainer",
  blogLocalSeoCost: "/blog/local-seo-cost-dc-area",
  blogWebsiteAuditAnatomy: "/blog/anatomy-of-a-website-audit",
  blogTrafficNoLeads: "/blog/website-traffic-no-leads",

  // Spanish
  homeEs: "/es",
  aboutEs: "/es/sobre-nosotros",
  contactEs: "/es/contacto",
  ctoEs: "/es/para-contratar/director-de-tecnologia",
  especialistaEnMarketingDigitalEs: "/es/para-contratar/especialista-en-marketing-digital",
  gestorDeContenidoWebEs: "/es/para-contratar/gestor-de-contenido-web",
  gestorDeContenidosEs: "/es/para-contratar/gestor-de-contenidos",
  especialistaEnContenidoDigitalEs: "/es/para-contratar/especialista-en-contenido-digital",
  especialistaEnContenidoEcommerceEs: "/es/para-contratar/especialista-en-contenido-ecommerce",
  gerenteDeMarketingDeContenidosEs: "/es/para-contratar/gerente-de-marketing-de-contenidos",
  sociosDeNegocioEs: "/es/socios-de-negocio",
  privacyPolicyEs: "/es/politica-de-privacidad",
  termsOfServiceEs: "/es/terminos-de-servicio",
  accessibilityEs: "/es/accesibilidad",
  reviewsEs: "/es/resenas",
  agreementsEs: "/es/acuerdos",
  agreementWebsitePlanEs: "/es/acuerdos/plan-de-sitio-web",
  agreementOnlineStorePlanEs: "/es/acuerdos/plan-de-tienda-en-linea",
  agreementGoogleMapsEs: "/es/acuerdos/optimizacion-google-maps",
  agreementSocialMediaEs: "/es/acuerdos/gestion-de-redes-sociales",
  agreementLocalSeoPagesEs: "/es/acuerdos/paginas-de-seo-local",
  agreementSpecialistRetainerEs: "/es/acuerdos/contrato-de-especialista-en-marketing",
  agreementFixedScopeProjectEs: "/es/acuerdos/proyecto-de-alcance-fijo",
  serviciosEs: "/es/servicios",
  desarrolloWebEs: "/es/servicios/desarrollo-web",
  seoLocalEs: "/es/servicios/seo-local",
  seoTecnicoEs: "/es/servicios/seo-tecnico",
  googleBusinessProfileEs: "/es/servicios/google-business-profile",
  keywordResearchEs: "/es/servicios/investigacion-de-palabras-clave",
  gestionRedesSocialesEs: "/es/servicios/gestion-redes-sociales",
  transmisionesEnVivoEs: "/es/servicios/transmisiones-en-vivo",
  moderacionDeChatEs: "/es/servicios/moderacion-de-chat",
  produccionRemotaEs: "/es/servicios/produccion-remota",
  marketingDeCrecimientoEs: "/es/servicios/marketing-de-crecimiento",
  marketingDigitalEs: "/es/servicios/marketing-digital",
  fotografiaEs: "/es/servicios/fotografia",
  fotografiaComidaEs: "/es/servicios/fotografia/comida",
  fotografiaModeloEs: "/es/servicios/fotografia/modelo",
  sitiosWebBufetesEs: "/es/servicios/sitios-web-bufetes",
  sitiosWebEcommerceEs: "/es/servicios/sitios-web-ecommerce",
  desarrolloWebShopifyEs: "/es/servicios/desarrollo-web-shopify",
  desarrolloDeAppsMovilesEs: "/es/servicios/desarrollo-de-apps-moviles",
  desarrolloDeMarcaEs: "/es/servicios/desarrollo-de-marca",
  solucionesAwsEs: "/es/servicios/soluciones-aws",
  productosEs: "/es/productos",
  industriasEs: "/es/industrias",
  industriasRestaurantesEs: "/es/industrias/restaurantes",
  industriasAbogadosEs: "/es/industrias/abogados",
  industriasEcommerceEs: "/es/industrias/ecommerce",
  industriasMarcasDeModaEs: "/es/industrias/marcas-de-moda",
  industriasSalonesEs: "/es/industrias/salones",
  industriasCuidadoInfantilEs: "/es/industrias/cuidado-infantil",
  portafolioEs: "/es/portafolio",
  auditoriaWebEs: "/es/auditoria-web",
  contratameEs: "/es/contratame",
  paraContratarEs: "/es/para-contratar",
  especialistaMarketingCrecimientoEs: "/es/para-contratar/especialista-en-marketing-de-crecimiento",
  revisionGoogleEs: "/es/revision-google",
  agendarEs: "/es/agendar",
  agendarGraciasEs: "/es/agendar/gracias",
  enVivoEs: "/es/en-vivo",
  enlacesEs: "/es/enlaces",
  directorioEs: "/es/directorio",
  reporteDeMuestraEs: "/es/reporte-de-muestra",
  blogEs: "/es/blog",
  blogCoordinatorVsRetainerEs: "/es/blog/coordinador-de-marketing-o-iguala",
  blogLocalSeoCostEs: "/es/blog/costo-seo-local-dc",
  blogWebsiteAuditAnatomyEs: "/es/blog/anatomia-de-una-auditoria-web",
  blogTrafficNoLeadsEs: "/es/blog/trafico-sin-clientes",
} as const;
